import type { AuditEvent, AuditListQuery, Change } from '@central/api';
import { formatDateTime } from '@central/ui';

/** The audit log's URL search: every filter is optional. */
export interface AuditSearch {
  customerId?: string;
  actor?: string;
  action?: string;
  target?: string;
  range?: AuditRange;
}

export const auditRanges = [
  { value: '24h', label: 'Last 24 hours', ms: 24 * 3600_000 },
  { value: '7d', label: 'Last 7 days', ms: 7 * 24 * 3600_000 },
  { value: '30d', label: 'Last 30 days', ms: 30 * 24 * 3600_000 },
  { value: '90d', label: 'Last 90 days', ms: 90 * 24 * 3600_000 },
] as const;

export type AuditRange = (typeof auditRanges)[number]['value'];

export function isAuditRange(v: unknown): v is AuditRange {
  return auditRanges.some((r) => r.value === v);
}

/** The GET /audit query for a search; no range means all time. */
export function auditQuery(s: AuditSearch, now = Date.now()): AuditListQuery {
  const range = auditRanges.find((r) => r.value === s.range);
  return {
    customerId: s.customerId || undefined,
    actor: s.actor || undefined,
    action: s.action || undefined,
    target: s.target || undefined,
    from: range ? new Date(now - range.ms).toISOString() : undefined,
  };
}

const iso = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/;

/** A diff value as one line of text: times localised, objects as compact JSON. */
export function describeValue(v: unknown): string {
  if (v === undefined || v === null || v === '') return '–';
  if (typeof v === 'string') return iso.test(v) ? formatDateTime(v) : v;
  if (typeof v === 'boolean') return v ? 'true' : 'false';
  if (typeof v === 'number') return String(v);
  return JSON.stringify(v, (_, x: unknown) =>
    typeof x === 'string' && iso.test(x) ? formatDateTime(x) : x,
  );
}

/** "settings.logAccess: owners → all"; a value only on one side shows alone. */
export function describeAuditChange(c: Change): string {
  if (c.from === undefined) return `${c.path}: ${describeValue(c.to)}`;
  if (c.to === undefined) return `${c.path}: ${describeValue(c.from)} → –`;
  return `${c.path}: ${describeValue(c.from)} → ${describeValue(c.to)}`;
}

/** Who acted: an email when recorded, else the id, else the kind ("system"). */
export function actorLabel(e: AuditEvent): string {
  return e.actor.email ?? e.actor.id ?? e.actor.kind;
}

/** The console page a target names, when it has one. */
export function targetLink(
  target: string,
):
  | { to: '/customers/$customerId'; params: { customerId: string } }
  | { to: '/licenses/$licenseId'; params: { licenseId: string } }
  | { to: '/environments/$environmentId'; params: { environmentId: string } }
  | undefined {
  const i = target.indexOf('/');
  if (i < 0) return undefined;
  const coll = target.slice(0, i);
  const id = target.slice(i + 1);
  if (!id) return undefined;
  switch (coll) {
    case 'customers':
      return { to: '/customers/$customerId', params: { customerId: id } };
    case 'licenses':
      return { to: '/licenses/$licenseId', params: { licenseId: id } };
    case 'environments':
      return { to: '/environments/$environmentId', params: { environmentId: id } };
    default:
      return undefined;
  }
}
